import React from 'react'
import { Box, Stack, Typography, alpha, useTheme } from '@mui/material'
import { linearProgressClasses } from '@mui/material/LinearProgress'
import { Check, X } from 'lucide-react'

import { Progress, PROGRESS_BAR_GRADIENT } from 'components/display/Progress'
import { PASSWORD_REQUIREMENTS, getPasswordStrength } from 'constants/passwordPolicy'

export interface PasswordStrengthMeterProps {
  password: string
}

/**
 * VERA-1095 items 3 + 5 — shown under `PasswordSetup`'s "Create password" /
 * "New password" field.
 *
 * Reuses the shared `Progress` bar (`components/display/Progress`, extended
 * here with its new `hideLabels` prop rather than hand-rolled — house rule:
 * improve the shared component before reaching for a new one) for the bar
 * itself, and renders `PASSWORD_REQUIREMENTS` as an explicit checklist below
 * it. Colour is never the only signal — a colour-blind user, or anyone
 * glancing past the bar, still gets the checklist and its check/✕ glyphs —
 * and the bar can never read "Strong" on a password the submit gate would
 * reject: both this meter and `passwordMeetsPolicy` (the actual submit gate,
 * in `PasswordSetup`) read the exact same `PASSWORD_REQUIREMENTS` array.
 */
export const PasswordStrengthMeter = ({ password }: PasswordStrengthMeterProps) => {
  const theme = useTheme()
  const strength = getPasswordStrength(password)
  // The unmet state. `text.disabled` for both the ✕ glyph and its label, so
  // the two halves of an unmet row cannot drift apart, and so "unmet" reads as
  // the same weight as any other disabled affordance in the app.
  const unmetColor = theme.palette.text.disabled

  return (
    // VERA-1095: 12px, not the 4px this was tightened to for the signup page's
    // no-vertical-scroll-at-1280x680 budget. At 4px the gap matched the 6px
    // rowGap BETWEEN the check rows, so the checklist read as a fifth row of
    // the bar rather than as its own group. The separation has to beat the
    // internal rhythm to group at all; 12px is double it (and the §29 value).
    <Stack spacing={3}>
      <Progress
        value={strength.metCount}
        min={0}
        max={strength.total}
        // The house gradient, at every fill level — the same bar the billing page
        // draws. `color` is assigned straight to `background`, so handing it a
        // gradient is not a hack; it is the one way to get the DEFAULT bar while
        // still using this branch (which is what carries `rounded`).
        //
        // Previously this was `strength.color`: a white alpha while filling. On the
        // light theme that is invisible against the track, so the bar read as empty
        // no matter how much of the password was typed.
        color={PROGRESS_BAR_GRADIENT}
        showCircles={false}
        hideLabels
        rounded="all"
        sx={{
          // The unfilled TRACK. `Progress`'s own default is `grey[100]`, which
          // reads as a filled bar on a dark page rather than as the absence of
          // one. Derived from `text.primary` so it inverts with the page: a
          // faint light wash on dark, a faint dark wash on light. Either way it
          // is the quietest mark on screen, which is what an empty track is.
          [`&.${linearProgressClasses.colorPrimary}`]: {
            backgroundColor: alpha(theme.palette.text.primary, 0.1),
          },
        }}
      />
      {/* Two columns, not one tall list. Five stacked rows added ~120px to a form
          that is already tall enough to push the submit button down the page, and
          the labels are short ("One number"), so the width was going spare.
          Collapses to a single column below 400px, where two columns would start
          wrapping "One special character" onto a second line and cost more height
          than it saved. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          columnGap: '16px',
          rowGap: '6px',
        }}
      >
        {PASSWORD_REQUIREMENTS.map((requirement) => {
          const met = password.length > 0 && requirement.test(password)
          return (
            <Stack key={requirement.id} direction="row" spacing={1} alignItems="center">
              {met ? (
                // flexShrink guards the icon against being squeezed by a label
                // that wraps — the defect that made the plan-card checkmarks
                // render at inconsistent sizes.
                // Vera purple, matching the bar's completion colour — see the
                // colour rationale in `getPasswordStrength`. Brand purple is
                // mode-invariant by design, so this one does NOT flip: a met
                // requirement is the brand's own colour, not a surface. The
                // unmet ✕ stays neutral so the two states differ in hue AND
                // lightness rather than in hue alone.
                <Check
                  size={14}
                  strokeWidth={2.5}
                  color={theme.palette.brand[600]}
                  style={{ flexShrink: 0 }}
                />
              ) : (
                <X size={14} strokeWidth={2} color={unmetColor} style={{ flexShrink: 0 }} />
              )}
              <Typography
                sx={{
                  fontSize: '12px',
                  lineHeight: 1.4,
                  // Met is deliberately NOT `text.primary`: the checklist is
                  // supporting copy under the field, so even a satisfied row
                  // should sit just below the page's brightest text.
                  color: met ? alpha(theme.palette.text.primary, 0.85) : unmetColor,
                }}
              >
                {requirement.label}
              </Typography>
            </Stack>
          )
        })}
      </Box>
    </Stack>
  )
}

export default PasswordStrengthMeter
